import React,{useState, useEffect} from 'react';
import { Box, Button, Divider, Paper, Typography, makeStyles } from '@material-ui/core';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@material-ui/icons';
import Helper from '../../../common/Helper';
import Loader from '../../../sharedComponents/Loader/Loader';

const useStyles = makeStyles((theme) => ({
    reviewContainer: {
        width: '100%',
        maxHeight: 'calc(100vh - 220px)',
        overflowY: 'auto',
        background: '#f0f2f5',
        padding: theme.spacing(2.5),
        borderRadius: theme.spacing(1),
    },
    reviewHeader: {
        background: 'linear-gradient(135deg, #1d428a 0%, #2758b8 100%)',
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(2.5, 3),
        marginBottom: theme.spacing(2.5),
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    reviewScoreRing: {
        width: 80,
        height: 80,
        borderRadius: '50%',
        border: '4px solid rgba(255,255,255,0.35)',
        background: 'rgba(255,255,255,0.12)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewLegend: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },
    reviewLegendItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 1.5),
        borderRadius: theme.spacing(2),
        background: '#ffffff',
        border: '1px solid #e0e0e0',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    },
    reviewLegendSwatch: {
        width: 10,
        height: 10,
        borderRadius: '50%',
    },
    reviewCard: {
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        background: '#ffffff',
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        border: '1px solid #e8eaf0',
    },
    reviewQuestionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    reviewAnswerGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(0.5),
    },
    reviewAnswer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        borderRadius: theme.spacing(0.75),
        borderLeft: '3px solid transparent',
        transition: 'background 0.15s',
        flex: '1 1 calc(50% - 8px)',
        minWidth: 160,
        boxSizing: 'border-box',
    },
    reviewHelpBox: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.5),
        borderRadius: theme.spacing(0.75),
    },
    reviewActionBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: theme.spacing(1),
        position: 'sticky',
        bottom: 0,
    }
}));

const QuizReviewPage = ({ questions, completedQuizQuestions, getSubmittedAnswersForQuestion, handleReviewSubmit, showContinueButton = true }) => {

    const classes = useStyles();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
       if(questions.length > 0 && completedQuizQuestions.length > 0){
        setIsLoading(false);
       }
    }, [questions, completedQuizQuestions]);

    return (
        <Box className={classes.reviewContainer}>
            {/* ── Score Header ── */}
            {(() => {
                const correctCount = (questions || []).filter(q => {
                    const cq = (completedQuizQuestions || []).find(item => parseInt(item.questionID) === parseInt(q.id));
                    return cq?.isCorrect === true || cq?.isCorrect === 1;
                }).length;
                const totalCount = (questions || []).length;
                const pct = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
                const passBg = pct >= 70
                    ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)'
                    : 'linear-gradient(135deg, #1d428a 0%, #2758b8 100%)';
                return (
                    <Box className={classes.reviewHeader} style={{ background: passBg }}>
                        <Box>
                            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 4 }}>Quiz Review</Typography>
                            <Typography variant="body2" style={{ opacity: 0.85 }}>
                                {pct >= 70 ? 'Great work! Review your answers below.' : 'Review your answers and see where you can improve.'}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                            <Box className={classes.reviewScoreRing}>
                                <Typography variant="h5" style={{ fontWeight: 800, lineHeight: 1, color: '#fff' }}>{pct}<span style={{ fontSize: '0.6em', fontWeight: 600 }}>%</span></Typography>
                                <Typography variant="caption" style={{ opacity: 0.8, color: '#fff', fontSize: '0.65rem' }}>SCORE</Typography>
                            </Box>
                            <Box textAlign="right">
                                <Typography variant="h6" style={{ fontWeight: 700, lineHeight: 1 }}>{correctCount} / {totalCount}</Typography>
                                <Typography variant="caption" style={{ opacity: 0.8, fontSize: '0.72rem' }}>Questions Correct</Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            })()}

            {/* ── Legend ── */}
            <Box className={classes.reviewLegend}>
                <Box className={classes.reviewLegendItem}>
                    <Box className={classes.reviewLegendSwatch} style={{ background: '#2e7d32' }} />
                    <Typography variant="caption" style={{ fontWeight: 500 }}>Correct Answer</Typography>
                </Box>
                <Box className={classes.reviewLegendItem}>
                    <Box className={classes.reviewLegendSwatch} style={{ background: '#c62828' }} />
                    <Typography variant="caption" style={{ fontWeight: 500 }}>Your Incorrect Selection</Typography>
                </Box>
                <Box className={classes.reviewLegendItem}>
                    <Box className={classes.reviewLegendSwatch} style={{ background: '#e0e0e0', border: '1px solid #bdbdbd' }} />
                    <Typography variant="caption" style={{ fontWeight: 500 }}>Not Selected</Typography>
                </Box>
            </Box>

            {/* ── Question Cards ── */}
            {(questions || []).map((question, index) => {
                const submittedAnswerIds = getSubmittedAnswersForQuestion(question.id);
                const completedQuestion = (completedQuizQuestions || []).find((item) => item?.questionID == question.id);
                const isCorrect = completedQuestion?.isCorrect === true || completedQuestion?.isCorrect === 1;
                const helpText = isCorrect ? question.rightAnswerHelpText : question.wrongAnswerHelpText;

                return (
                    <Paper key={question.id} className={classes.reviewCard} elevation={0}>
                        <Box className={classes.reviewQuestionHeader}>
                            <Box display="flex" flexDirection='column' style={{ gap: 8 }}>
                                <Box component='div' display='flex' flexGrow={1} flexDirection='row'>
                                    <Box style={{
                                        background: '#1d428a', color: '#fff',
                                        borderRadius: 20, padding: '2px 12px',
                                        fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap'
                                    }}>
                                        Q {index + 1}
                                    </Box>
                                    <Typography variant="subtitle2" style={{ fontWeight: 600, lineHeight: 1.4, marginLeft: 8 }}>
                                        {Helper.stripHtmlTags(question?.questionTitle ?? '')}
                                    </Typography>
                                </Box>
                                <Box component='div'>
                                    <Typography variant="caption" style={{ fontSize: '0.65rem', color: '#616161' }}>
                                        {question?.questionDescription ?? ''}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                background: isCorrect ? '#e8f5e9' : '#ffebee',
                                color: isCorrect ? '#2e7d32' : '#c62828',
                                borderRadius: 20, padding: '3px 10px',
                                fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap',
                                border: `1px solid ${isCorrect ? '#a5d6a7' : '#ef9a9a'}`,
                            }}>
                                {isCorrect
                                    ? <><CheckCircleIcon style={{ fontSize: 14, marginRight: 3 }} /> Correct</>
                                    : <><CancelIcon style={{ fontSize: 14, marginRight: 3 }} /> Incorrect</>
                                }
                            </Box>
                        </Box>

                        <Divider style={{ margin: '8px 0 10px' }} />

                        <Box className={classes.reviewAnswerGrid}>
                            {(question.answers || []).map((ans) => {
                                const isUserSelected = submittedAnswerIds.includes(ans.id);
                                let bg, borderColor, textColor, Icon = null;

                                if (isCorrect && isUserSelected) {
                                    bg = '#f1f8f1'; borderColor = '#2e7d32'; textColor = '#1b5e20';
                                    Icon = <CheckCircleIcon style={{ fontSize: 18, color: '#2e7d32', flexShrink: 0 }} />;
                                } else if (!isCorrect && isUserSelected) {
                                    bg = '#fdf1f1'; borderColor = '#c62828'; textColor = '#b71c1c';
                                    Icon = <CancelIcon style={{ fontSize: 18, color: '#c62828', flexShrink: 0 }} />;
                                } else {
                                    bg = '#fafafa'; borderColor = 'transparent'; textColor = '#616161';
                                    Icon = null;
                                }

                                return (
                                    <Box
                                        key={ans.id}
                                        className={classes.reviewAnswer}
                                        style={{ background: bg, borderLeftColor: borderColor }}
                                    >
                                        {Icon}
                                        <Typography variant="body2" style={{ color: textColor, fontWeight: ans.isCorrect || isUserSelected ? 500 : 400 }}>
                                            {ans.answer}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {submittedAnswerIds.length === 0 && (
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, fontStyle: 'italic' }}>No answer submitted.</Typography>
                        )}

                        {helpText && (
                            <Box className={classes.reviewHelpBox} style={{
                                background: isCorrect ? '#e8f5e9' : '#fff3e0',
                                border: `1px solid ${isCorrect ? '#c8e6c9' : '#ffe0b2'}`,
                            }}>
                                <Typography variant="caption" style={{
                                    color: isCorrect ? '#2e7d32' : '#e65100',
                                    fontWeight: 600, letterSpacing: '0.4px', whiteSpace: 'nowrap',
                                }}>
                                    {isCorrect ? '✓ TIP' : '! NOTE'}
                                </Typography>
                                <Typography variant="body2" style={{ color: isCorrect ? '#1b5e20' : '#bf360c', lineHeight: 1.5 }}>
                                    {helpText}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                );
            })}

            {showContinueButton && (
                <Box className={classes.reviewActionBar}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleReviewSubmit}
                        style={{ minWidth: 140, fontWeight: 600, borderRadius: 24, padding: '8px 32px' }}
                    >
                        Continue
                    </Button>
                </Box>
            )}
            <Loader isLoading={isLoading} />
        </Box>
    );
};

export default QuizReviewPage;